import type {
  Facility,
  FacilityGroupID,
  FacilityID,
  FacilityStorageAreaType,
  FacilityStorageCapacityUnit,
  FacilityStorageTypeID,
  FacilityStorageLocationID,
  FacilityStorageSecondaryContainmentType,
  FacilityStorageVesselID,
  PermissionID,
  PermissionTypeID,
  SimpleSelectable,
  TenantID,
  UserID,
} from '../cross-app-shared/types/db';
import type { Email, ISO8601DateOnly, ISO8601Timestamp, Uid } from '../cross-app-shared/types/common';
import { crossAppBackendRequest } from './cross-app-backend-request';

export const crossAppActions = {
  users: {
    refreshStorageAccess(args: { tenantID: TenantID }) {
      return crossAppBackendRequest('storageAccessRefresh', args);
    },
    you() {
      return crossAppBackendRequest('you', {});
    },

    youUpdate(args: { name: string }) {
      return crossAppBackendRequest('youUpdate', args);
    },

    userUpdate(args: {
      userID: UserID;
      name: string;
      email: Email;
      primaryTenantID: TenantID | null;
      primaryFacilityGroupID: FacilityGroupID | null;
      primaryFacilityID: FacilityID | null;
    }) {
      return crossAppBackendRequest('userUpdate', args);
    },

    listForManagement() {
      return crossAppBackendRequest('usersListForManagement', {});
    },

    async listForTenant(
      tenantID: TenantID,
    ): Promise<{ uid: Uid; email: Email; name: string; facilityName: string; primaryFacilityID: FacilityID | null }[]> {
      if (tenantID) {
        return crossAppBackendRequest('usersListForTenant', { tenantID });
      } else {
        return [];
      }
    },

    create(args: {
      name: string;
      email: Email;
      primaryTenantID: TenantID | null;
      primaryFacilityGroupID: FacilityGroupID | null;
      primaryFacilityID: FacilityID | null;
    }) {
      return crossAppBackendRequest('userCreate', args);
    },

    permissionAdd(args: {
      userID: UserID;
      permissionTypeID: PermissionTypeID;
      tenantID: TenantID | null;
      facilityGroupID: FacilityGroupID | null;
      facilityID: FacilityID | null;
    }) {
      return crossAppBackendRequest('userPermissionAdd', args);
    },

    permissionRemove(permissionID: PermissionID) {
      return crossAppBackendRequest('userPermissionRemove', { permissionID });
    },

    facilityPermissionSummary(args: { facilityID: FacilityID }) {
      return crossAppBackendRequest('facilityPermissionSummary', args);
    },

    ensureAccessOnTestSite() {
      return crossAppBackendRequest('userEnsureAccessOnTestSite', {});
    },

    createDemoTenantIfNecessary(): Promise<void> {
      return crossAppBackendRequest('userCreateDemoTenantIfNecessary', {});
    },
  },

  facilities: {
    list() {
      return crossAppBackendRequest('facilitiesList', {});
    },

    detailsGet(facilityID: FacilityID) {
      return crossAppBackendRequest('facilityDetailsGet', { facilityID });
    },

    detailsUpdate(args: SimpleSelectable<Facility>) {
      return crossAppBackendRequest('facilityDetailsUpdate', args);
    },

    create(args: SimpleSelectable<Facility>) {
      return crossAppBackendRequest('facilityCreate', args);
    },

    listGroups() {
      return crossAppBackendRequest('facilityGroupsList', {});
    },

    groupCreate(args: { tenantID: TenantID; groupName: string }) {
      return crossAppBackendRequest('facilityGroupCreate', args);
    },

    contactsForAllInTenant(tenantID: TenantID) {
      return crossAppBackendRequest('facilityContactsForAllInTenant', { tenantID });
    },
  },

  storageLocations: {
    list(args: { facilityID: FacilityID; includeArchived?: boolean }) {
      return crossAppBackendRequest('facilityStorageLocationsList', args);
    },

    save(args: {
      facilityStorageLocationID: FacilityStorageLocationID | null;
      facilityID: FacilityID;
      name: string;
      areaType: FacilityStorageAreaType | null;
      isHazWasteRegulated?: boolean;
      isSPCCRegulated?: boolean;
      isTier2Regulated?: boolean;
      description: string;
    }) {
      return crossAppBackendRequest('facilityStorageLocationSave', args);
    },

    archive(args: { facilityID: FacilityID; facilityStorageLocationID: FacilityStorageLocationID; archived: boolean }) {
      return crossAppBackendRequest('facilityStorageLocationArchive', args);
    },

    siteMapGet(args: { facilityID: FacilityID }) {
      return crossAppBackendRequest('facilityStorageLocationSiteMapGet', args);
    },

    siteMapSave(args: {
      facilityID: FacilityID;
      expectedUpdatedAt: ISO8601Timestamp | null;
      centerLat: number;
      centerLng: number;
      zoom: number;
      features: import('../cross-app-shared/types/storage-site-map.ts').StorageSiteMapFeatureCollection;
    }) {
      return crossAppBackendRequest('facilityStorageLocationSiteMapSave', args);
    },

    siteMapGeocodeFacility(args: { facilityID: FacilityID }) {
      return crossAppBackendRequest('facilityStorageLocationSiteMapGeocodeFacility', args);
    },

    siteMapGeocodeSearch(args: { facilityID: FacilityID; query: string }) {
      return crossAppBackendRequest('facilityStorageLocationSiteMapGeocodeSearch', args);
    },

    resolveCompatibilityConcern(args: {
      facilityID: FacilityID;
      facilityStorageLocationID: FacilityStorageLocationID;
      pairKey: string;
      note: string;
    }) {
      return crossAppBackendRequest('facilityStorageLocationCompatibilityResolutionSave', args);
    },
  },

  /** Physical containers/vessels (drums, tanks, totes, etc.) - many per storage location. The
   * SAME shared collection Tier II's per-product vessel lines link to via `vesselID`. */
  storageVessels: {
    list(args: { facilityID: FacilityID; facilityStorageLocationID?: FacilityStorageLocationID }) {
      return crossAppBackendRequest('facilityStorageVesselsList', args);
    },

    save(args: {
      facilityStorageVesselID: FacilityStorageVesselID | null;
      facilityID: FacilityID;
      facilityStorageLocationID: FacilityStorageLocationID | null;
      accumulationStartDate?: ISO8601DateOnly | null;
      storageTypeID?: FacilityStorageTypeID;
      capacityAmount?: number | null;
      capacityUnit?: FacilityStorageCapacityUnit | null;
      hasSecondaryContainment?: boolean;
      secondaryContainmentType?: FacilityStorageSecondaryContainmentType | null;
      vesselMaterialConstruction?:
        | '304 stainless steel'
        | '316 stainless steel'
        | 'hdpe'
        | 'ldpe'
        | 'polypropylene (pp)'
        | 'pvc'
        | 'pvdf'
        | 'cast iron'
        | 'epdm'
        | 'viton (fkm)'
        | 'ptfe'
        | null;
      description?: string;
    }) {
      return crossAppBackendRequest('facilityStorageVesselSave', args);
    },

    archive(args: { facilityID: FacilityID; facilityStorageVesselID: FacilityStorageVesselID; archived: boolean }) {
      return crossAppBackendRequest('facilityStorageVesselArchive', args);
    },
  },
};
